import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import ArrowDown from 'shared-components/icons/ArrowDown';
import { useElementDimensions } from '../../../../../utils/hooks/use-element-dimensions';
import {
  normalizedContractsShape,
  priceBreakdownObject
} from '../../../app-bookings-request/forms/bookingUtil';
import { PricingSkeleton } from '../../common/items/PricingSkeleton';
import { BookingPriceBreakdown } from '../../common/items/booking-price-breakdown/BookingPriceBreakdown';

export const UpdateSummaryPriceDiff = ({
  currentContracts,
  pendingContracts,
  profile,
  showSkeleton
}) => {
  const {
    spaceRentalContract: pendingSpaceRentalContract,
    masterContract: pendingMasterContract,
    items: pendingAddOnsContracts
  } = pendingContracts;
  const booking = pendingSpaceRentalContract?.meta?.booking;
  const listing = pendingSpaceRentalContract?.meta?.listing;
  const currentPriceBreakdown = priceBreakdownObject(currentContracts);
  const pendingPriceBreakdown = priceBreakdownObject(pendingContracts);

  const { updateDifference } = pendingPriceBreakdown;
  const additionalPayment = updateDifference?.additional_payment || 0;
  const totalRefund = updateDifference?.refund || 0;
  const priceDiff = totalRefund ? totalRefund * -1 : additionalPayment;

  const [setPriceBreakdownRef, priceBreakdownDims] = useElementDimensions();
  const [showPriceBreakdown, setShowPriceBreakdown] = useState(false);
  const [priceBreakdownStyles, setPriceBreakdownStyles] = useState({
    overflow: 'hidden',
    transition: 'all 0.3s ease',
    height: '0px'
  });

  useEffect(() => {
    if (!showPriceBreakdown) {
      return;
    }
    setPriceBreakdownStyles((prevStyles) => ({
      ...prevStyles,
      // transition should be none for smooth transtions of
      // nested collapsible sections (e.g PriceBreakdownTaxesAndFees)
      transition: 'none',
      height: `${priceBreakdownDims.height}px`
    }));
  }, [priceBreakdownDims.height]);

  useEffect(() => {
    setPriceBreakdownStyles((prevStyles) => ({
      overflow: 'hidden',
      transition: 'all 0.3s ease',
      height:
        prevStyles.height === '0px' ? `${priceBreakdownDims.height}px` : '0px'
    }));
  }, [showPriceBreakdown]);

  return (
    <div
      className='padding-xl-left padding-xl-right'
      data-testing-id='update-summary-price-diff'
    >
      <div className='margin-lg-bottom'>
        <span>
          <FormattedMessage
            id='booking_update_summary.original_price'
            defaultMessage='Original price'
          />
        </span>
        <span
          className='pull-right'
          data-testing-id='update-summary-original-price'
        >
          <FormattedNumber
            value={currentPriceBreakdown.guestPayment || 0}
            currency={pendingPriceBreakdown.currency}
            style='currency'
          />
        </span>
      </div>
      {showSkeleton ? (
        <PricingSkeleton />
      ) : (
        <div data-testing-id='new-price'>
          <div className='margin-lg-bottom'>
            <span>
              <FormattedMessage
                id='booking_update_summary.new_price'
                defaultMessage='New price'
              />
            </span>
            <span
              className='pull-right'
              data-testing-id='update-summary-new-price'
            >
              <FormattedNumber
                value={pendingPriceBreakdown.guestPayment || 0}
                currency={pendingPriceBreakdown.currency}
                style='currency'
              />
            </span>
          </div>
          <div>
            <span
              data-testing-id='price-difference-button'
              className='text-base heavier middle'
              role='button'
              style={{
                display: 'inline-flex',
                cursor: 'pointer'
              }}
              onClick={() => setShowPriceBreakdown((prev) => !prev)}
            >
              <FormattedMessage
                id='booking_update_summary.price_difference'
                defaultMessage='Price difference'
              />
              <span
                className='margin-sm-left flex middle center'
                style={{
                  transition: 'all 0.2s ease',
                  transform:
                    priceBreakdownStyles.height === '0px'
                      ? 'rotate(-90deg)'
                      : 'none'
                }}
              >
                <ArrowDown />
              </span>
            </span>
            <span
              className='pull-right heavier'
              data-testing-id='update-summary-diff-amount'
            >
              <FormattedNumber
                value={priceDiff || 0}
                currency={pendingPriceBreakdown.currency}
                style='currency'
              />
            </span>
            <div
              data-testing-id='fee-calc-container'
              className='margin-md-top'
              style={priceBreakdownStyles}
              onTransitionEnd={() => {
                if (priceBreakdownStyles.height !== '0px') {
                  setPriceBreakdownStyles((prev) => ({
                    ...prev,
                    overflow: 'visible'
                  }));
                }
              }}
            >
              <div ref={setPriceBreakdownRef}>
                <BookingPriceBreakdown
                  booking={booking}
                  listing={listing}
                  profile={profile}
                  masterContract={pendingMasterContract}
                  addOnsContracts={pendingAddOnsContracts}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

UpdateSummaryPriceDiff.propTypes = {
  profile: PropTypes.object.isRequired,
  currentContracts: normalizedContractsShape,
  pendingContracts: normalizedContractsShape,
  showSkeleton: PropTypes.bool
};
